// EntityNode.tsx — the React Flow custom node for the non-asset kinds surfaced
// by the graph explorer (documents, interventions, expenses, contracts,
// contacts). It is a deliberately smaller card than AssetNode: a kind icon,
// the entity name, and a kind-colored left border.
//
// A body click (or Enter/Space) never navigates — it toggles loading the
// entity's own connected nodes (the same action the small (+)/(−) button
// performs), generalizing the intervention-only expansion that used to be the
// sole affordance here. Navigation to the entity's detail page is opt-in
// through a dedicated open-in-new icon, mirroring AssetNode's `graph-open-*`
// button. Interventions have no detail route, so they render no open icon —
// their card only ever expands to their expenses.

import { memo } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import {
    mdiAccountGroupOutline,
    mdiPlusCircleOutline,
    mdiMinusCircleOutline,
    mdiOpenInNew
} from '@mdi/js'

import { kindColorVar } from './colors'
import type { GraphNode, EntityKind } from './adapter'
import { SECTION_ICONS } from '../../lib/section-icons'

const KIND_ICON: Record<EntityKind, string> = {
    document: SECTION_ICONS.documents,
    intervention: SECTION_ICONS.interventions,
    expense: SECTION_ICONS.expenses,
    contact: mdiAccountGroupOutline,
    contract: SECTION_ICONS.contracts,
}

export interface EntityNodeData extends Record<string, unknown> {
    graph: GraphNode
    loading: boolean
    // Whether this kind has anything resolvable to expand (every current kind
    // does — intervention → expenses, the rest → their own neighbor
    // expansion — but the flag is kept so a future kind with genuinely
    // nothing to load can opt out of the clickable affordance without
    // silently swallowing a click that looks actionable).
    canToggle: boolean
    expanded: boolean
    // Body click / Enter-Space / the (+)/(−) button: load this entity's
    // connected nodes (intervention → its expenses via `ivexp@`, everything
    // else → its own neighbor expansion via `ENTITY_EXPAND_ABBR`).
    onToggle: (kind: EntityKind, entityUid: string) => void
    // Explicit open-in-new icon only. undefined ⇒ no icon rendered
    // (interventions have no detail route).
    onOpen?: (kind: EntityKind, entityUid: string) => void
}

function EntityNodeComponent({ data }: NodeProps) {
    const { t } = useTranslation('graph')
    const d = data as EntityNodeData
    const node = d.graph
    const kind = node.kind as EntityKind
    const accent = kindColorVar(kind)

    const toggle = () => {
        if (!d.canToggle || !node.entityUid) return
        d.onToggle(kind, node.entityUid)
    }

    return (
        <div
            data-testid={`graph-entity-${kind}-${node.entityUid}`}
            role="button"
            tabIndex={0}
            title={node.name}
            aria-pressed={d.canToggle ? d.expanded : undefined}
            onClick={toggle}
            onKeyDown={(e) => {
                if (!d.canToggle) return
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggle()
                }
            }}
            className={`relative w-[150px] rounded-md border bg-card text-card-foreground shadow-sm ${
                d.canToggle ? 'cursor-pointer hover:bg-accent/40' : ''
            }`}
            style={{ borderColor: accent, borderLeftWidth: 4 }}
        >
            <Handle type="target" position={Position.Top} className="!bg-muted-foreground/40" />
            <Handle type="source" position={Position.Bottom} className="!bg-muted-foreground/40" />

            <div className="flex items-center gap-1.5 p-2">
                <AppIcon path={KIND_ICON[kind]} size="sm" style={{ color: accent }} className="shrink-0" />
                <div className="min-w-0 flex-1">
                    <div
                        className="truncate text-xs font-medium"
                        data-testid={`graph-entity-name-${node.entityUid}`}
                    >
                        {node.name}
                    </div>
                    <div className="truncate text-[10px] text-muted-foreground">{t(`kind.${kind}`)}</div>
                </div>
                {d.canToggle && (
                    <button
                        type="button"
                        data-testid={`graph-entity-expand-${node.entityUid}`}
                        aria-label={d.expanded ? t('node.collapse') : t('node.expand')}
                        title={d.expanded ? t('node.collapse') : t('node.expand')}
                        aria-pressed={d.expanded}
                        disabled={d.loading}
                        onClick={(e) => {
                            e.stopPropagation()
                            toggle()
                        }}
                        className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                    >
                        <AppIcon path={d.expanded ? mdiMinusCircleOutline : mdiPlusCircleOutline} size="xs" />
                    </button>
                )}
                {d.onOpen && (
                    <button
                        type="button"
                        data-testid={`graph-entity-open-${node.entityUid}`}
                        aria-label={t('node.open_entity')}
                        title={t('node.open_entity')}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (node.entityUid) d.onOpen!(kind, node.entityUid)
                        }}
                        className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    >
                        <AppIcon path={mdiOpenInNew} size="xs" />
                    </button>
                )}
            </div>
        </div>
    )
}

export const EntityNode = memo(EntityNodeComponent)
